import React from 'react';
import Cookies from 'js-cookie';
import { Subscription } from 'rxjs';
import Layout from 'components/Layout';
import { PageContextType } from 'types';
import { AuthenticationProvider } from 'components/Authentication';
import { FlagsProvider } from 'context/flagsProvider';
import { AppContext } from '../context/AppContext';
import { getGDPRStatus } from '../helpers/geolocation';
import { SegmentService } from '../analytics';
import { PageDataType, PageNotFoundDataType } from './types';

type AllProvidersStateType = {
    hasGivenCookieConsent: boolean;
};

const textToBooleanMapping: { [index: string]: boolean } = {
    yes: true,
    no: false,
};
type AllProvidersProps = {
    page: PageDataType | PageNotFoundDataType;
    pageContext: PageContextType;
};

function isNormalPage(page: PageDataType | PageNotFoundDataType): page is PageDataType {
    return !!(page as PageDataType).body;
}

export class AllProviders extends React.Component<AllProvidersProps, AllProvidersStateType> {
    geolocation$: Subscription;

    state = {
        /**
         * Setting this to true by default so there is no flickering in the UI
         */
        hasGivenCookieConsent: true,
    };

    componentDidMount() {
        this.geolocation$ = getGDPRStatus().subscribe((GDPRStatus: boolean) => {
            // If the user is part of the GDPR, then show the cookie consent banner
            // if they haven't already accepted the terms
            if (GDPRStatus === true && Cookies.get('cookieconsent_status') !== 'dismiss') {
                this.setState({ hasGivenCookieConsent: false });
            }
        });

        SegmentService.page(this.props.page);
    }
    componentWillUnmount() {
        this.geolocation$.unsubscribe();
    }

    handleCookieConsent = () => {
        Cookies.set('cookieconsent_status', 'dismiss', {
            path: '/',
            domain: process.env.COOKIE_DOMAIN,
            expires: 365,
        });
        this.setState({ hasGivenCookieConsent: true });
    };

    render() {
        const { page, pageContext, children } = this.props;
        const options = isNormalPage(page)
            ? {
                  show_navigation_header: textToBooleanMapping[page.show_navigation_header],
                  header_type: page.header_type,
                  partner_logo: page.partner_logo,
                  partner_url: page.partner_url,
                  show_footer: textToBooleanMapping[page.show_footer],
              }
            : {
                  show_navigation_header: true,
                  header_type: 'Main Navigation',
                  partner_logo: null,
                  partner_url: null,
                  show_footer: true,
              };
        // Unpublished pages don't contain data at compile time, so we need defaults here.
        const layoutData = {
            alternateLanguages: page._meta.alternateLanguages || [],
            homePage: (isNormalPage(page) && page.home_page) || '',
            lang: page._meta.lang || 'en-us',
        };

        return (
            <FlagsProvider>
                <AppContext.Provider
                    value={{
                        pageContext: this.props.pageContext,
                        alternateLanguages: layoutData.alternateLanguages,
                        options,
                        homePage: layoutData.homePage,
                    }}
                >
                    <AuthenticationProvider>
                        <Layout
                            // TODO: remove these props and use app context instead
                            lang={layoutData.lang}
                            alternateLanguages={layoutData.alternateLanguages}
                            options={options}
                            homePage={layoutData.homePage}
                            pageContext={pageContext}
                            hasGivenCookieConsent={this.state.hasGivenCookieConsent}
                            handleCookieConsent={this.handleCookieConsent}
                        >
                            {children}
                        </Layout>
                    </AuthenticationProvider>
                </AppContext.Provider>
            </FlagsProvider>
        );
    }
}
